/**
 * The CRM side of the RingCentral widget (plan §D5): what the widget has told
 * us about itself, and the one way a call is started — from the lead header,
 * through startCall().
 *
 * RingCentralDock feeds widget events in through noteWidgetEvent(); React reads
 * the state with useRingCentral(). Field names are the ones the step 1 event
 * log showed (plan §0 item 3).
 *
 * CALL END (step 3): rc-active-call-notify (or rc-ringout-call-notify) with
 * call.terminationType "final", keyed on call.telephonySessionId, fires in every
 * calling mode. Whether anyone picked up comes from the session events seen on
 * the way (CallConnected, parties[].status.code "Answered",
 * parties[].missedCall). The finished call goes to the handler the dock
 * registers (POST /api/ringcentral/calls), once per session.
 */

import { useSyncExternalStore } from 'react';
import { PHONE_QUERY } from '../hooks/usePhoneWidth';

/** RingCentral is set up (VITE_RINGCENTRAL_CLIENT_ID). Texting keys off this — everywhere, mobile included. */
export const ringCentralEnabled = !!(import.meta.env.VITE_RINGCENTRAL_CLIENT_ID ?? '').trim();

/**
 * MOBILE: a phone-width screen or a touch-first device (phone, tablet).
 * Decided once, at page load, so turning or resizing the window never loads or
 * removes the phone mid-call. A touchscreen laptop with a mouse or trackpad
 * is not mobile (its primary pointer is fine and it can hover).
 */
export const isMobileDevice = (() => {
  try {
    return window.matchMedia(PHONE_QUERY).matches || window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  } catch {
    return false;
  }
})();

/**
 * The Embeddable phone (widget, badge, panel, call logging): desktop only. On
 * mobile the script is never loaded and Call opens the phone's own dialer (tel:).
 */
export const ringCentralPhoneEnabled = ringCentralEnabled && !isMobileDevice;

/** The microphone permission as the browser reports it to this page. */
export type MicState = 'granted' | 'prompt' | 'denied' | 'unknown';

export interface RingCentralState {
  loggedIn: boolean;
  /** 'browser' | 'ringout' | 'jupiter' | 'softphone' — the user's choice in the widget. */
  callWith: string | null;
  dialerReady: boolean;
  mic: MicState;
  /** A message for the user, shown by RingCentralDock; null when there is none. */
  notice: { kind: 'info' | 'warn'; text: string } | null;
  /** The user's RingOut "My Location" — a number of OURS, never a client's. */
  myLocation: string | null;
  /** Mobile: the RingCentral app did not open — offer the phone's dialer (tel:). */
  dialFallback: { tel: string; number: string } | null;
}

/** A finished call, as the browser saw it — the body of POST /api/ringcentral/calls. */
export interface EndedCall {
  telephony_session_id: string;
  session_id: string | null;
  direction: 'Inbound' | 'Outbound';
  from_number: string | null;
  to_number: string | null;
  started_at: number | null;
  ended_at: number;
  result: 'answered' | 'missed' | 'no_answer';
  call_with: string | null;
  rc_extension_id: string | null;
  lead_id: string | null;
  exclude_numbers: string[];
}

/**
 * The call the header just started: which lead, which number. finish() pairs
 * it with the widget's call so it is logged on the file the rep was on.
 */
export interface PendingCall { leadId: string; phone: string; at: number }

let state: RingCentralState = { loggedIn: false, callWith: null, dialerReady: false, mic: 'unknown', notice: null, myLocation: null, dialFallback: null };
let pending: PendingCall | null = null;
const listeners = new Set<() => void>();

function set(patch: Partial<RingCentralState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

export function useRingCentral(): RingCentralState {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    () => state,
  );
}

export const pendingCall = (): PendingCall | null => pending;
export const clearNotice = () => set({ notice: null });
export const showNotice = (kind: 'info' | 'warn', text: string) => set({ notice: { kind, text } });
export const setMicState = (mic: MicState) => set({ mic });
export const clearDialFallback = () => set({ dialFallback: null });

// ---- call tracking ----------------------------------------------------------

interface Tracked {
  sessionId: string | null;
  direction: 'Inbound' | 'Outbound' | null;
  from: string | null;
  to: string | null;
  startTime: number | null;
  answered: boolean;
  missed: boolean;
  extensionId: string | null;
}
const tracked = new Map<string, Tracked>();
const finished = new Set<string>();
let onCallEnded: ((call: EndedCall) => void) | null = null;

/** The dock's handler for a finished call (logs it, opens the notes modal). */
export function setCallEndedHandler(fn: ((call: EndedCall) => void) | null) { onCallEnded = fn; }

const last10 = (raw: unknown) => String(raw ?? '').replace(/\D/g, '').slice(-10);

function track(id: string): Tracked {
  let t = tracked.get(id);
  if (!t) {
    t = { sessionId: null, direction: null, from: null, to: null, startTime: null, answered: false, missed: false, extensionId: null };
    tracked.set(id, t);
  }
  return t;
}

/** rc-active-call-notify / rc-ringout-call-notify: the call itself. */
function noteCall(call: any) {
  const id = typeof call?.telephonySessionId === 'string' ? call.telephonySessionId : null;
  if (!id || finished.has(id)) return;
  const t = track(id);
  if (call.direction === 'Inbound' || call.direction === 'Outbound') t.direction = call.direction;
  t.from = call.from?.phoneNumber ?? t.from;
  t.to = call.to?.phoneNumber ?? t.to;
  t.sessionId = call.sessionId ?? t.sessionId;
  if (typeof call.startTime === 'number') t.startTime = call.startTime;
  if (call.telephonyStatus === 'CallConnected') t.answered = true;
  if (call.terminationType === 'final') finish(id, t);
}

/** rc-telephony-session-notify: answered / missed, and whose extension. */
function noteSession(session: any) {
  const id = typeof session?.id === 'string' ? session.id : null;
  if (!id || finished.has(id)) return;
  const t = track(id);
  for (const p of Array.isArray(session.parties) ? session.parties : []) {
    if (p?.status?.code === 'Answered') t.answered = true;
    if (p?.missedCall) t.missed = true;
    if (p?.extensionId && !t.extensionId) t.extensionId = String(p.extensionId);
  }
  if (!t.extensionId && session.extensionId) t.extensionId = String(session.extensionId);
}

function finish(id: string, t: Tracked) {
  finished.add(id);
  tracked.delete(id);
  if (!t.direction) return;

  const client = t.direction === 'Outbound' ? t.to : t.from;
  // The header's Call button names the lead — if this is that number, recently.
  let leadId: string | null = null;
  if (pending && t.direction === 'Outbound' && last10(pending.phone) === last10(client)
      && Date.now() - pending.at < 30 * 60_000) {
    leadId = pending.leadId;
    pending = null;
  }

  const result: EndedCall['result'] =
    t.direction === 'Inbound' ? (t.answered && !t.missed ? 'answered' : 'missed')
    : t.answered ? 'answered' : 'no_answer';

  onCallEnded?.({
    telephony_session_id: id,
    session_id: t.sessionId,
    direction: t.direction,
    from_number: t.from,
    to_number: t.to,
    started_at: t.startTime,
    ended_at: Date.now(),
    result,
    call_with: state.callWith,
    rc_extension_id: t.extensionId,
    lead_id: leadId,
    exclude_numbers: state.myLocation ? [state.myLocation] : [],
  });
}

/** Called by RingCentralDock for every `rc-` message from the widget. */
export function noteWidgetEvent(data: any) {
  switch (data?.type) {
    case 'rc-active-call-notify':
    case 'rc-ringout-call-notify':
      noteCall(data.call);
      break;
    case 'rc-telephony-session-notify':
      noteSession(data.telephonySession);
      break;
    case 'rc-login-status-notify':
      set({ loggedIn: !!data.loggedIn, ...(data.loggedIn ? {} : { dialerReady: false }) });
      break;
    case 'rc-calling-settings-notify':
      set({
        callWith: typeof data.callWith === 'string' ? data.callWith : null,
        ...(typeof data.myLocation === 'string' ? { myLocation: data.myLocation } : {}),
      });
      break;
    case 'rc-dialer-status-notify':
      set({ dialerReady: !!data.ready });
      break;
  }
}

/** E.164 for the widget: 10 digits → +1…, 11 starting 1 → +…, a typed + kept. */
export function toE164(raw: unknown): string | null {
  const text = String(raw ?? '').trim();
  const digits = text.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (text.startsWith('+') && digits.length >= 8) return `+${digits}`;
  return null;
}

const MIC_BLOCKED =
  'Your browser is blocking the microphone, so RingCentral cannot make browser calls. ' +
  'Click the icon at the left of the address bar, set Microphone to Allow, then reload the page. ' +
  '(Or switch the phone to RingOut in its Settings → Calling.)';

export const micBlockedText = MIC_BLOCKED;

function adapter(): any { return (window as any).RCAdapter; }

function openWidget() {
  try { adapter()?.setClosed?.(false); adapter()?.setMinimized?.(false); } catch { /* not ready */ }
}

/**
 * Start a call from the lead header. THE ONLY PLACE A CALL STARTS.
 * Returns false when it did not dial, after telling the user why.
 */
export function startCall(phone: unknown, leadId: string): boolean {
  const e164 = toE164(phone);
  if (!e164) {
    set({ notice: { kind: 'warn', text: 'This phone number cannot be dialled — check it on the file.' } });
    return false;
  }
  if (!state.loggedIn) {
    openWidget();
    set({ notice: { kind: 'info', text: 'Sign in to RingCentral in the phone panel, then press Call again.' } });
    return false;
  }
  if (state.callWith === 'browser' && state.mic === 'denied') {
    openWidget();
    set({ notice: { kind: 'warn', text: MIC_BLOCKED } });
    return false;
  }
  const frame = document.querySelector<HTMLIFrameElement>('#rc-widget-adapter-frame');
  if (!frame?.contentWindow) {
    set({ notice: { kind: 'warn', text: 'The phone panel has not finished loading — try again in a moment.' } });
    return false;
  }

  pending = { leadId, phone: e164, at: Date.now() };
  openWidget();
  frame.contentWindow.postMessage({ type: 'rc-adapter-new-call', phoneNumber: e164, toCall: true }, 'https://apps.ringcentral.com');
  set({ notice: null });
  return true;
}

// ---- mobile: the RingCentral app ----------------------------------------------

/** Mobile calls go through the RingCentral app (the company number) when RingCentral is set up. */
export const ringCentralAppCalls = ringCentralEnabled && isMobileDevice;

/**
 * The RingCentral mobile app's call link, as RingCentral's URI scheme guide
 * gives it (developers.ringcentral.com/guide/voice/uri-scheme):
 * rcmobile://call?number=16501112222 — country code and digits, no "+".
 */
export function rcMobileCallUrl(phone: unknown): string | null {
  const e164 = toE164(phone);
  return e164 ? `rcmobile://call?number=${e164.slice(1)}` : null;
}

const APP_WAIT_MS = 2500;

/**
 * Call from a phone: open the RingCentral app (set from script — the guide's
 * advice for Chrome, where a plain link to the scheme is unreliable). A web
 * page cannot ask whether an app is installed, so: when the app opens, this
 * page goes to the background and nothing more happens; when it is still in
 * front APP_WAIT_MS later, the app did not open (not installed, or iOS's
 * "Open in RingCentral?" still waiting) and the RingCentralDock bar offers the
 * phone's dialer. It never dials the personal line on its own: with the
 * iOS prompt still up, that would call the client from the wrong number.
 */
export function callWithRingCentralApp(phone: unknown): void {
  const app = rcMobileCallUrl(phone);
  const e164 = toE164(phone);
  if (!app || !e164) {
    set({ notice: { kind: 'warn', text: 'This phone number cannot be dialled — check it on the file.' } });
    return;
  }
  set({ dialFallback: null });
  let left = false;
  const onHide = () => {
    if (document.visibilityState !== 'hidden') return;
    left = true;
    set({ dialFallback: null });
  };
  const onPageHide = () => { left = true; };
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', onPageHide);
  setTimeout(() => {
    document.removeEventListener('visibilitychange', onHide);
    window.removeEventListener('pagehide', onPageHide);
    if (!left && document.visibilityState === 'visible') set({ dialFallback: { tel: `tel:${e164}`, number: e164 } });
  }, APP_WAIT_MS);
  window.location.assign(app);
}
